import React from "react";
import { Form, Button, Checkbox, Dropdown } from "semantic-ui-react";
import { POSITIONS } from "../../../../utils/tips";
import { useFormik } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import { useAuth, useUser } from "../../../../hooks";
import "./AddEditUserForm.scss";

const ROLE_OPTIONS = [
  { key: "STANDARD", value: "STANDARD", text: "Estándar" },
  { key: "ADMIN", value: "ADMIN", text: "Admin" },
  { key: "SUPERUSER", value: "SUPERUSER", text: "Súper Usuario" },
];

function getRole(isStaff, isSuperuser) {
  if (isSuperuser) return "SUPERUSER";
  if (isStaff) return "ADMIN";
  return "STANDARD";
}

function getRoleFlags(role) {
  if (role === "SUPERUSER") return { is_staff: true, is_superuser: true };
  if (role === "ADMIN") return { is_staff: true, is_superuser: false };
  return { is_staff: false, is_superuser: false };
}

export function AddEditUserForm(props) {
  const { onClose, onRefetch, user } = props;
  const { addUser, updateUser } = useUser();
  const { auth } = useAuth();
  // Súper Usuario: todos los roles. Admin: al CREAR puede elegir Estándar o
  // Admin (no Súper Usuario); al editar, solo el rol actual de esa cuenta (y
  // Estándar) — ascender una cuenta existente es del Súper Usuario.
  const currentRole = user ? getRole(user.is_staff, user.is_superuser) : "ADMIN";
  const roleOptions = auth.me?.is_superuser
    ? ROLE_OPTIONS
    : ROLE_OPTIONS.filter(
        (option) => option.value === "STANDARD" || option.value === currentRole
      );


  const formik = useFormik({
    initialValues: initialValues(user),
    validationSchema: Yup.object(user ? updateSchame() : newSchame()),
    validateOnChange: false,
    onSubmit: async (formValue) => {
      try {
        const { role, confirmPassword, ...rest } = formValue;
        const dataToSend = { ...rest, ...getRoleFlags(role) };
        if (!dataToSend.can_login) {
          dataToSend.email = "";
          dataToSend.password = "";
        }

        if (user) await updateUser(user.id, dataToSend);
        else await addUser(dataToSend);

        toast.success(user ? "Usuario actualizado" : "Usuario creado");
        onRefetch();
        onClose();
      } catch (error) {
        console.error(error);
        toast.error(
          "No se pudo guardar el usuario. Revisa los datos e intenta de nuevo."
        );
      }
    },
  });

  return (
    <Form className="add-edit-user-form" onSubmit={formik.handleSubmit} autoComplete="off">
      {/* Señuelo: el navegador rellena aquí su correo y contraseña guardados en
          vez de hacerlo en los campos reales, que deben abrir en blanco. */}
      <div aria-hidden="true" style={{ position: "absolute", left: -9999, height: 0, overflow: "hidden" }}>
        <input type="text" name="fake-username" tabIndex={-1} autoComplete="username" />
        <input type="password" name="fake-password" tabIndex={-1} autoComplete="current-password" />
      </div>
      <Form.Input
        name="username"
        placeholder="Nombre de usuario (sin espacios ni tildes, ej: jperez)"
        autoComplete="off"
        value={formik.values.username}
        onChange={formik.handleChange}
        error={formik.errors.username}
      />
      <Form.Input
        name="first_name"
        placeholder="Nombre"
        autoComplete="off"
        value={formik.values.first_name}
        onChange={formik.handleChange}
        error={formik.errors.first_name}
      />
      <Form.Input
        name="last_name"
        placeholder="Apellidos"
        autoComplete="off"
        value={formik.values.last_name}
        onChange={formik.handleChange}
        error={formik.errors.last_name}
      />

      {/* Persona registrada que nunca entra al sistema (ej. limpieza, o quien solo
          participa del reparto de propinas): sin correo ni contraseña. */}
      <div className="add-edit-user-form__active">
        <Checkbox
          toggle
          checked={formik.values.can_login}
          disabled={!!user && auth.me?.id === user.id}
          onChange={(_, data) => formik.setFieldValue("can_login", data.checked)}
        />
        Puede iniciar sesión
      </div>
      {!formik.values.can_login && (
        <p className="add-edit-user-form__hint">
          Queda registrada y participa del reparto de propinas, pero no entra al sistema: no
          necesita correo ni contraseña.
        </p>
      )}
      {formik.values.can_login && (
        <>
          <Form.Input
            name="email"
            placeholder="Correo electronico"
            autoComplete="off"
            value={formik.values.email}
            onChange={formik.handleChange}
            error={formik.errors.email}
          />
          <Form.Input
            name="password"
            type="password"
            autoComplete="new-password"
            placeholder={user ? "Contraseña nueva (vacío = no cambiarla)" : "Contraseña"}
            value={formik.values.password}
            onChange={formik.handleChange}
            error={formik.errors.password}
          />
          <Form.Input
            name="confirmPassword"
            type="password"
            autoComplete="new-password"
            placeholder="Repetir contraseña"
            value={formik.values.confirmPassword}
            onChange={formik.handleChange}
            error={formik.errors.confirmPassword}
          />
        </>
      )}

      <div className="add-edit-user-form__active">
        <Checkbox
          toggle
          checked={formik.values.is_active}
          onChange={(_, data) =>
            formik.setFieldValue("is_active", data.checked)
          }
        />
        Usuario activo
      </div>

      <div className="add-edit-user-form__role">
        <label>Rol</label>
        <Dropdown
          selection
          options={roleOptions}
          value={formik.values.role}
          onChange={(_, data) => formik.setFieldValue("role", data.value)}
        />
        {!user && !auth.me?.is_superuser && formik.values.role === "ADMIN" && (
          <p style={{ color: "#777", marginTop: 6 }}>
            Un Admin creado por ti no aparecerá en tu lista: solo el Súper
            Usuario podrá editarlo o borrarlo.
          </p>
        )}
      </div>

      {/* Puesto en el local (no es un permiso): para repartir propinas. */}
      <div className="add-edit-user-form__role">
        <label>Puesto{formik.values.role === "STANDARD" && !user ? " *" : ""}</label>
        <Dropdown
          selection
          clearable={formik.values.role !== "STANDARD"}
          placeholder={formik.values.role === "STANDARD" ? "Elige el puesto" : "Sin puesto"}
          options={POSITIONS}
          value={formik.values.position || null}
          error={!!formik.errors.position}
          onChange={(_, data) => formik.setFieldValue("position", data.value || "")}
        />
        {formik.errors.position && (
          <p style={{ color: "#c0392b", marginTop: 6 }}>{formik.errors.position}</p>
        )}
      </div>

      {/* Admin y Súper Usuario siempre cobran; un Estándar solo con esto
          (el cajero). Ver users/permissions.py. */}
      {formik.values.role === "STANDARD" && (
        <div className="add-edit-user-form__active">
          <Checkbox
            toggle
            checked={formik.values.can_charge}
            onChange={(_, data) => formik.setFieldValue("can_charge", data.checked)}
          />
          Puede cobrar (caja)
        </div>
      )}

      <Button
        type="submit"
        primary
        fluid
        content={user ? "Actualizar" : "Crear"}
      />
    </Form>
  );
}

// Un alta abre TODO en blanco (el usuario se crea activo y con acceso).
function initialValues(data) {
  const internalEmail = (data?.email || "").endsWith("@sin-acceso.local");
  return {
    username: data?.username || "",
    email: internalEmail ? "" : data?.email || "",
    first_name: data?.first_name || "",
    last_name: data?.last_name || "",
    password: "",
    confirmPassword: "",
    is_active: data ? !!data.is_active : true,
    role: getRole(data?.is_staff, data?.is_superuser),
    can_charge: data?.can_charge ? true : false,
    position: data?.position || "",
    can_login: data ? data.can_login !== false : true,
  };
}

const whenLogin = (schema, ifLogin) =>
  Yup.string().when("can_login", (canLogin) => (canLogin ? ifLogin : Yup.string()));

function newSchame() {
  return {
    username: Yup.string().required(true),
    email: whenLogin(null, Yup.string().email(true).required(true)),
    first_name: Yup.string(),
    last_name: Yup.string(),
    password: whenLogin(null, Yup.string().required(true)),
    confirmPassword: whenLogin(
      null,
      Yup.string()
        .oneOf([Yup.ref("password")], "Las contraseñas no coinciden")
        .required(true)
    ),
    is_active: Yup.bool().required(true),
    role: Yup.string().required(true),
    // Todo trabajador (Estándar) se crea con su puesto.
    position: Yup.string().when("role", (role, schema) =>
      role === "STANDARD" ? schema.required("Elige el puesto del trabajador.") : schema
    ),
  };
}

function updateSchame() {
  return {
    username: Yup.string().required(true),
    email: whenLogin(null, Yup.string().email(true).required(true)),
    first_name: Yup.string(),
    last_name: Yup.string(),
    password: Yup.string(),
    confirmPassword: Yup.string().oneOf(
      [Yup.ref("password")],
      "Las contraseñas no coinciden"
    ),
    is_active: Yup.bool().required(true),
    role: Yup.string().required(true),
  };
}
